import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ExpandMore from '@material-ui/icons/ExpandMore';
import withGraphqlLoaderHOC from 'components/theme/loader/with-graphql-loader-hoc';
import classnames from 'classnames';
import { graphql, withApollo } from 'react-apollo';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ComputerIcon from '@material-ui/icons/Computer';
import { withRouter } from 'react-router-dom';

import gqlQueryGetAppEnvs from 'graphql/queries/get-app-envs';
import clientGqlMutUpdateEnvContext from 'graphql/mutations/update-env-context';
import * as routeUtils from 'utils/routes';

import _get from 'lodash/get';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      textDecoration: 'none',
      position: 'relative',
      cursor: 'pointer'
    },
    collapsed: {
      maxHeight: 42,
      '& $listIcon': {
        marginLeft: 5
      }
    },
    show: {
      opacity: 1,
      transition: theme.transitions.create('opacity', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      })
    },
    hidden: {
      opacity: 0
    },
    selectEnv: {
      '& [role="document"]': {
        transform: 'translate(-5px, 70px) !important',
        width: 215
      }
    },
    envName: {
      position: 'relative',
      paddingRight: 20
    },
    textWrap: {
      overflowWrap: 'break-word',
      whiteSpace: 'normal'
    },
    envTitle: {},
    listIcon: {},
    icon: {
      position: 'absolute',
      top: '50%',
      right: 0,
      transform: 'translateY(-50%)',
      color: theme.palette.text.primary
    },
    prod: {
      background: theme.palette.environment.prod
    },
    other: {
      background: theme.palette.environment.other,
      '& $envName, & $envTitle, & $icon': {
        color: theme.palette.common.white
      }
    },
    dev: {
      background: theme.palette.environment.dev,
      '& $envName, & $envTitle, & $icon': {
        color: theme.palette.common.white
      }
    }
  };
});

function SelectEnvironment(props) {
  const [anchorEl, setAnchorEl] = useState(null);

  const classes = useStyles();
  const {
    env: envContext,
    app: appContext = {},
    collapse,
    listItemClass,
    history
  } = props;

  const envs = _get(props, 'data.queryGetAppEnvs', []);
  const envContextEnvName = envContext && envContext.name;

  const urlEnvName = useMemo(() => {
    return routeUtils.path.getEnvFromPath();
  }, [history.location.pathname]);

  const updateEnvContext = useCallback((uId) => {
    const { client } = props;
    const { appId } = appContext;

    const promise = client.mutate({
      mutation: clientGqlMutUpdateEnvContext,
      variables: {
        appId,
        envUid: uId
      }
    });

    promise.catch(console.warn);

    return promise;
  });

  const handleEnvChange = useCallback((uId) => () => {
    setAnchorEl(null);
    updateEnvContext(uId).then(() => {
      // Update url only if current path is with env
      const envName = envs.find((e) => e.uId === uId).name;
      const path = routeUtils.build.currentPathWithNewEnv(envName);
      if (path) {
        history.push(path);
      }
    });
  });

  // Setting env context whenever envs gets updated, eg. initially or when appId gets changed and fetched new envs
  useEffect(() => {
    // Not updating context if no envs exists/fetched
    if (!envs.length) {
      return;
    }

    // Set envContext if env exists in url OR as first env in list
    const env = envs.find((e) => e.name === urlEnvName);
    if (env) {
      updateEnvContext(env.uId);
    } else {
      updateEnvContext(envs[0].uId);
    }
  }, [JSON.stringify(envs)]);

  // Setting env context from URL on URL change
  useEffect(() => {
    //  Not updating context:
    //  - if env is not in url
    //  - OR if urlEnv is same as envContextName, e.g. url change due to redirection from applications on appContext change
    if (!urlEnvName || !envContextEnvName || envContextEnvName === urlEnvName) {
      return;
    }

    // Set envContext if env exists in url
    const env = envs.find((e) => e.name === urlEnvName);
    if (env) {
      updateEnvContext(env.uId);
    }
  }, [urlEnvName]);

  const handleEnvSelectOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEnvSelectClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <ListItem
        component="div"
        className={classnames(
          listItemClass,
          classes.root,
          classes[envContextEnvName]
            ? classes[envContextEnvName]
            : classes.other,
          { [classes.collapsed]: collapse }
        )}
        data-testid="select-environment-list-item"
      >
        {collapse && (
          <ListItemIcon
            data-testid="env-icon"
            onClick={handleEnvSelectOpen}
            className={classes.listIcon}
          >
            <ComputerIcon />
          </ListItemIcon>
        )}

        <ListItemText
          className={classnames({
            [classes.hidden]: collapse,
            [classes.show]: !collapse
          })}
          component="button"
          role="button"
          onClick={handleEnvSelectOpen}
          to=""
          data-testid="list-item-text"
        >
          <Typography
            variant="caption"
            gutterBottom
            className={classes.envTitle}
          >
            Environment
          </Typography>

          <Typography
            variant="h6"
            gutterBottom
            className={classnames(classes.envName, classes.textWrap)}
            data-testid="env-context"
          >
            {envContextEnvName || 'Select Environment'}
            <ExpandMore className={classes.icon} />
          </Typography>
        </ListItemText>

        <Menu
          id="select-environment"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleEnvSelectClose}
          className={classes.selectEnv}
        >
          {envs.map((envItem) => (
            <MenuItem
              value={envItem.name}
              key={envItem.uId}
              onClick={handleEnvChange(envItem.uId)}
              selected={envItem.name === envContextEnvName}
            >
              {envItem.name}
            </MenuItem>
          ))}
        </Menu>
      </ListItem>
    </>
  );
}

const selectEnWithGqlLoader = withGraphqlLoaderHOC(
  SelectEnvironment,
  [{ name: 'queryGetAppEnvs', data: 'listEnvironments.items' }],
  {
    showLoader: false,
    showSelfOnLoading: true,
    showSelfOnError: true
  }
);

const selectEnvComp = graphql(gqlQueryGetAppEnvs, {
  name: 'queryGetAppEnvs',
  skip: ({ app }) => !(app && app.appId),
  options: ({ app }) => ({
    variables: {
      appId: app.appId
    }
  })
})(selectEnWithGqlLoader);

const selectEnvCompWithApollo = withApollo(selectEnvComp);

export default withRouter(selectEnvCompWithApollo);
